import Http from 'common/services/Http';
import { BudgetConfigDefaultDto, BudgetDto, BudgetToPdfDto } from './models/BudgetDto';
import IdVersion from 'api/common/types/IdVersion';
import { Paged } from 'api/common/types/Page';
import { BudgetsSearchCriteria } from './models/BudgetsSearchCriteria';
import { BudgetLineParameterByElementDto, BudgetLineParameterDto } from './models/BudgetLineParameterDto';
import { BudgetWorkDataDto } from './models/BudgetWorkDataDto';
import { BudgetHistoriesDto } from './models/BudgetHistoriesDto';
import BudgetGenerateReferenceViewModel from './models/BudgetGenerateReferenceViewModel';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';
import { ExportMaterialsListDto } from './models/ExportMaterialsListDto';

class BudgetsService {
    public getList(criteria: BudgetsSearchCriteria) {
        return Http.get<Paged<BudgetDto>>('budgets', criteria);
    }

    public getCatalog() {
        return Http.get<SelectInputOption[]>('budgets/catalog');
    }

    public getById(id: string, isDetails: boolean) {
        return Http.get<BudgetDto>('budgets/' + id + '/' + isDetails);
    }

    public getBudgetHistoryByVersion(budgetId: string, historyId: string) {
        return Http.get<BudgetDto>('budgetHistories', { budgetId, historyId });
    }

    public getHistoriesList(budgetId: string) {
        return Http.get<BudgetHistoriesDto[]>('budgetHistories/list-histories/' + budgetId);
    }

    public create(model: BudgetDto): Promise<string> {
        return Http.post('budgets', model);
    }

    public update(model: BudgetDto, forceHistoryVersion = false, historyReason?: string) {
        return Http.put('budgets', { ...model, forceHistoryVersion, historyReason });
    }

    public remove(model: IdVersion) {
        return Http.put('budgets/deactivate', model);
    }

    public getLastColumnMapping(externalPartId: string) {
        return Http.get<string | null>('budgets/last-column-mappings/' + externalPartId);
    }

    public getElementParameters(elementId: string,getChildren: boolean) {
        return Http.get<BudgetLineParameterByElementDto>('budgets/element-parameters/' + elementId + '/' + getChildren);
    }

    public getWorkData(workId: string) {
        return Http.get<BudgetWorkDataDto>('budgets/work-data/' + workId);
    }

    public printBudget(model: BudgetToPdfDto): Promise<Blob> {
        // 'download' is just for the path,it is not used for anything
        return Http.getWithConfig('budgets/print-budget/download', model, { responseType: 'blob' });
    }

    public getPrintBudgetUrl(model: BudgetToPdfDto, name: string): string {
        return Http.createUrl('budgets/print-budget/' + name, model);
    }

    public restoreFromVersion(budgetId: string, historyId: string, incrementDisplayVersion: boolean) {
        return Http.post('budgets/restore-from-version', { budgetId, historyId, incrementDisplayVersion });
    }

    public generateVersion(id: string) {
        return Http.post(`budgets/${id}/generate-version`, {});
    }

    public generateRevision(id: string) {
        return Http.post(`budgets/${id}/generate-revision`, {});
    }

    public generateReference(model: BudgetGenerateReferenceViewModel) {
        return Http.get<{ reference: string }>('budgets/generate-reference', model);
    }

    public getConfigDefaults () {
        return Http.get<BudgetConfigDefaultDto[]>('budgets/config-defaults');
    }

    public saveConfigDefaults (defaults: BudgetConfigDefaultDto[]) {
        return Http.put('budgets/config-defaults', defaults);
    }

    public downloadBudget(file: Blob, fileName: string) {
        const url = URL.createObjectURL(file);
        const anchor = document.createElement('a');
        anchor.download = `Devis_${fileName}.pdf`;
        anchor.href = url;
        anchor.target = '_blank'
        anchor.click();
    }

    public exportMaterialsList(model: ExportMaterialsListDto): Promise<Blob> {
        return Http.postWithConfig('budgets/materials-list/export', model, { responseType: 'blob' });
    }

    public downloadBlob(file: Blob, fileName: string) {
        const url = URL.createObjectURL(file);
        const anchor = document.createElement('a');
        anchor.download = fileName;
        anchor.href = url;
        anchor.target = '_blank';
        anchor.click();
        URL.revokeObjectURL(url);
    }
}

export default new BudgetsService();
